import React from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import SwitchCameraOutlinedIcon from "@mui/icons-material/SwitchCameraOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HighQualityOutlinedIcon from "@mui/icons-material/HighQualityOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

const GUIDE_ITEMS = [
  {
    icon: <MicOutlinedIcon />,
    title: "Microphone",
    description:
      "Mute or unmute your microphone during the consultation.",
  },
  {
    icon: <VideocamOutlinedIcon />,
    title: "Camera",
    description:
      "Turn your camera on or off during an Audio & Video consultation.",
  },
  {
    icon: <CallEndOutlinedIcon />,
    title: "Leave Call",
    description:
      "Leave the current call. If consultation access is still available, you can choose to return.",
    danger: true,
  },
  {
    icon: <SwitchCameraOutlinedIcon />,
    title: "Switch Camera",
    description:
      "Change between available cameras when your device supports more than one camera.",
  },
  {
    icon: <VisibilityOutlinedIcon />,
    title: "Self View",
    description:
      "Hide or show your own video preview. This does not stop your camera.",
  },
  {
    icon: <HighQualityOutlinedIcon />,
    title: "Video Quality",
    description:
      "Choose the video optimization mode when video is available.",
  },
  {
    icon: <WifiOutlinedIcon />,
    title: "Connection Quality",
    description:
      "Shows the current call connection as Good, Fair, or Poor. A warning appears if poor connection continues.",
  },
  {
    icon: <ChatOutlinedIcon />,
    title: "Chat",
    description:
      "Open consultation messages and appointment comments without leaving the call.",
  },
  {
    icon: <DescriptionOutlinedIcon />,
    title: "Reports",
    description:
      "View reports and appointment documents during the consultation.",
  },
  {
    icon: <PrivacyTipOutlinedIcon />,
    title: "Privacy",
    description:
      "Do not record, screenshot, redistribute, or share consultation content without appropriate permission.",
  },
];

const CallGuideDialog = ({
  open,
  onClose,
}) => {
  const theme = useTheme();

  const fullScreen =
    useMediaQuery(
      theme.breakpoints.down("sm"),
    );

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="call-guide-title"
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: "calc(100% - 48px)",
            md: "760px",
          },

          maxWidth: {
            sm: 720,
            md: 820,
          },

          maxHeight: {
            xs: "100%",
            sm: "88vh",
          },

          m: {
            xs: 0,
            sm: 2,
          },

          borderRadius: {
            xs: 0,
            sm: 3,
          },
        },
      }}
    >
      <DialogTitle
        id="call-guide-title"
        sx={{
          pb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
        >
          Consultation Call Guide
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
          }}
        >
          Quick guide to the controls and tools available during your consultation.
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          py: {
            xs: 2,
            sm: 2.5,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },

            gap: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          {GUIDE_ITEMS.map(
            (item) => (
              <Box
                key={item.title}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,

                  p: {
                    xs: 1.5,
                    sm: 2,
                  },

                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,

                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,

                    flexShrink: 0,

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    borderRadius: "50%",

                    bgcolor: item.danger
                      ? "rgba(211,47,47,0.08)"
                      : "rgba(25,118,210,0.08)",

                    color: item.danger
                      ? "error.main"
                      : "primary.main",
                  }}
                >
                  {item.icon}
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{
                      mt: 0.25,
                      lineHeight: 1.55,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ),
          )}
        </Box>

        <Box
          sx={{
            mt: 2.5,

            display: "flex",
            alignItems: "flex-start",
            gap: 1,

            p: 1.5,

            borderRadius: 2,

            bgcolor:
              "rgba(46,125,50,0.06)",
          }}
        >
          <CheckCircleOutlineOutlinedIcon
            color="success"
            fontSize="small"
            sx={{
              mt: 0.1,
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Leaving the call does not automatically complete the appointment.
            You can return while consultation access is still available.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          py: {
            xs: 1.5,
            sm: 2,
          },

          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          fullWidth={fullScreen}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallGuideDialog;